import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';
import { CLIENT_ID } from '../../lib/client-context';
import { collaboratorKeys } from './collaboratorKeys';
import {
  Collaborator,
  AddCollaboratorInput,
  RemoveCollaboratorInput,
  InviteUserInput,
  HasCollaboratorsResponse,
} from './collaboratorTypes';
import { getConnectionState } from '../../contexts/ConnectionStateContext';
import { db } from '../../lib/offline-db';
import { useQueryErrorHandler } from '../useQueryErrorHandler';
import { batchInvalidateQueries } from '../../utils/queryInvalidationUtils';
import { cacheUser } from '../../utils/userCache';

/**
 * Hook for fetching collaborators for a project
 * Supports offline mode by using IndexedDB when offline
 */
export function useCollaborators(projectId: string | null) {
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useQuery({
    queryKey: collaboratorKeys.list({ projectId: projectId || '' }),
    queryFn: async () => {
      if (!projectId) {
        const error = new Error('Project ID is required');
        handleError(error, {
          action: 'silent',
          context: { operation: 'fetchCollaborators' },
        });
        throw error;
      }

      const fetchId = Date.now().toString().slice(-4);
      logger.fetch(`Starting collaborators fetch #${fetchId} (online: ${isOnline})`);
      const startTime = performance.now();

      try {
        // If offline, get data from IndexedDB
        if (!isOnline) {
          logger.fetch(
            `Offline mode: fetching collaborators from IndexedDB for project #${projectId}`
          );

          try {
            // Get collaborators from IndexedDB
            const collaborators = await db.collaborators
              .where('project_id')
              .equals(projectId)
              .toArray();

            logger.fetch(
              `Offline mode: loaded ${collaborators.length} collaborators from IndexedDB`
            );
            return collaborators;
          } catch (offlineError) {
            logger.error(`Error fetching offline collaborators #${fetchId}:`, offlineError);
            return []; // Return empty array for offline mode to prevent UI errors
          }
        }

        // Online mode - fetch from edge function
        logger.fetch('Fetching collaborators via edge function', { projectId });

        const { data, error } = await supabase.functions.invoke('get-project-collaborators', {
          body: { projectId },
        });

        if (error) {
          logger.error('Error calling get-project-collaborators edge function', {
            error: JSON.stringify(error),
            statusText: error.message,
            projectId,
          });
          throw error;
        }

        if (!data || !data.data) {
          logger.warn('No collaborator data returned from edge function', { projectId });
          return [];
        }

        // Transform the data from the edge function to match our expected structure
        const collaborators: Collaborator[] = data.data.map((collab: any) => ({
          id: collab.id,
          project_id: collab.project_id,
          user_id: collab.user_id,
          role: collab.role,
          users_with_details: collab.users_with_details || null,
        }));

        // Store collaborators in IndexedDB for offline access
        try {
          await db.collaborators.bulkPut(collaborators);
          logger.fetch(
            `Stored ${collaborators.length} collaborators in IndexedDB for project ${projectId}`
          );
        } catch (storageError) {
          logger.error(`Error storing collaborators in IndexedDB:`, storageError);
          // Continue even if storage fails - this is non-critical
        }

        // Cache user details for use in other parts of the app
        collaborators.forEach(collab => {
          if (collab.user_id && collab.users_with_details) {
            cacheUser(collab.user_id, {
              id: collab.user_id,
              email: collab.users_with_details.email || '',
              display_name: collab.users_with_details.display_name || '',
              avatar_url: collab.users_with_details.avatar_url || undefined,
            });
          }
        });

        // Log performance metrics
        const endTime = performance.now();
        const duration = endTime - startTime;
        logger.fetch(`Collaborators fetch #${fetchId} completed in ${duration.toFixed(2)}ms`);

        return collaborators;
      } catch (error) {
        // Use the error handler to handle and log the error
        const appError = handleError(error, {
          action: 'silent',
          context: {
            fetchId,
            operation: 'fetchCollaborators',
            projectId,
            isOnline,
          },
        });

        throw appError;
      }
    },
    enabled: !!projectId,
  });
}

/**
 * Hook for fetching pending invitations for a project
 */
export function useInvitations(projectId: string | null) {
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useQuery({
    queryKey: collaboratorKeys.invitation(projectId || ''),
    queryFn: async () => {
      if (!projectId) {
        const error = new Error('Project ID is required');
        handleError(error, {
          action: 'silent',
          context: { operation: 'fetchInvitations' },
        });
        throw error;
      }

      // Skip if offline - this is non-critical data for offline mode
      if (!isOnline) {
        logger.fetch(`Offline mode: skipping invitations fetch for project ${projectId}`);
        return [];
      }

      try {
        // Query only the fields we need
        const { data, error } = await supabase
          .from('project_invitations')
          .select('id, project_id, invitee_email, status, created_at')
          .eq('project_id', projectId)
          .eq('status', 'pending');

        if (error) {
          logger.error('Error fetching invitations', {
            error: error.message,
            projectId,
          });
          throw error;
        }

        return data || [];
      } catch (err) {
        const appError = handleError(err, {
          action: 'silent',
          context: { operation: 'fetchInvitations', projectId },
        });
        throw appError;
      }
    },
    enabled: !!projectId && isOnline,
  });
}

/**
 * Hook for checking if a project has any collaborators
 */
export function useHasCollaborators(projectId: string | null) {
  // Get connection state without causing hook instance creation
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useQuery<HasCollaboratorsResponse>({
    queryKey: collaboratorKeys.hasCollaboratorsForProject(projectId || ''),
    queryFn: async () => {
      if (!projectId) {
        return { hasCollaborators: false };
      }

      // Skip detailed check if offline - use stored collaborators if available
      if (!isOnline) {
        try {
          const count = await db.collaborators.where('project_id').equals(projectId).count();

          return { hasCollaborators: count > 0 };
        } catch (offlineError) {
          logger.error('Error checking offline collaborators', offlineError);
          return { hasCollaborators: false };
        }
      }

      try {
        const { error, count } = await supabase
          .from('project_collaborators')
          .select('*', { count: 'exact' })
          .eq('project_id', projectId);

        if (error) {
          logger.error('Error checking project collaborators', {
            error: error.message,
            projectId,
          });
          return { hasCollaborators: false };
        }

        return { hasCollaborators: count > 0 };
      } catch (error) {
        // Log error but don't fail the app
        handleError(error, {
          action: 'silent',
          context: { operation: 'checkHasCollaborators', projectId },
        });
        return { hasCollaborators: false };
      }
    },
    enabled: !!projectId,
    // Short stale time since this is non-critical data
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook for adding a collaborator to a project
 */
export function useAddCollaborator() {
  const queryClient = useQueryClient();
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async ({
      projectId,
      userId,
      role = 'guest',
    }: AddCollaboratorInput): Promise<Collaborator> => {
      const { data, error } = await supabase
        .from('project_collaborators')
        .insert([
          {
            project_id: projectId,
            user_id: userId,
            role,
            metadata: {
              client_id: CLIENT_ID,
              added_at: new Date().toISOString(),
            },
          },
        ])
        .single();

      if (error) {
        throw error;
      }
      return data;
    },
    onSuccess: (data, variables) => {
      const projectId = variables.projectId;

      // Invalidate collaborator queries for this project
      batchInvalidateQueries(
        queryClient,
        new Map([
          [collaboratorKeys.list({ projectId }), { exact: true }],
          [collaboratorKeys.hasCollaboratorsForProject(projectId), { exact: true }],
        ])
      );

      // Also invalidate the related project query to update shared status
      queryClient.invalidateQueries({
        queryKey: ['projects', 'detail', projectId],
      });
    },
    onError: (error, variables) => {
      handleError(error, {
        action: 'toast',
        context: {
          operation: 'addCollaborator',
          projectId: variables.projectId,
          userId: variables.userId,
        },
      });
    },
  });
}

/**
 * Hook for removing a collaborator from a project
 */
export function useRemoveCollaborator() {
  const queryClient = useQueryClient();
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async ({ projectId, userId }: RemoveCollaboratorInput): Promise<void> => {
      const { error } = await supabase
        .from('project_collaborators')
        .delete()
        .eq('project_id', projectId)
        .eq('user_id', userId);

      if (error) {
        throw error;
      }
    },
    onSuccess: (_, variables) => {
      const projectId = variables.projectId;

      // Invalidate collaborator queries for this project
      batchInvalidateQueries(
        queryClient,
        new Map([
          [collaboratorKeys.list({ projectId }), { exact: true }],
          [collaboratorKeys.hasCollaboratorsForProject(projectId), { exact: true }],
        ])
      );

      // Also invalidate the related project query to update shared status
      queryClient.invalidateQueries({
        queryKey: ['projects', 'detail', projectId],
      });
    },
    onError: (error, variables) => {
      handleError(error, {
        action: 'toast',
        context: {
          operation: 'removeCollaborator',
          projectId: variables.projectId,
          userId: variables.userId,
        },
      });
    },
  });
}

/**
 * Hook for inviting a user to a project
 */
export function useInviteUser() {
  const queryClient = useQueryClient();
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async ({ projectId, email, role = 'guest' }: InviteUserInput): Promise<void> => {
      const { error } = await supabase.functions.invoke('invite-user', {
        body: { projectId, email, role },
      });

      if (error) {
        throw error;
      }
    },
    onSuccess: (_, variables) => {
      const projectId = variables.projectId;

      // Invalidate invitation queries for this project
      queryClient.invalidateQueries({
        queryKey: collaboratorKeys.invitation(projectId),
      });
    },
    onError: (error, variables) => {
      handleError(error, {
        action: 'toast',
        context: {
          operation: 'inviteUser',
          projectId: variables.projectId,
          email: variables.email,
        },
      });
    },
  });
}

/**
 * Hook for preloading collaborator data
 * This is useful for ensuring user data is available for task assignees, etc.
 */
export function usePreloadCollaborators() {
  const { data: collaborators, isLoading, isFetching } = useCollaborators(null);
  const queryClient = useQueryClient();

  const preload = async (projectId: string): Promise<void> => {
    if (!projectId) return;

    logger.store('Preloading collaborators for project', { projectId });

    try {
      // Prefetch collaborators for this project
      await queryClient.prefetchQuery({
        queryKey: collaboratorKeys.list({ projectId }),
        queryFn: async () => {
          // Use the edge function to fetch collaborators
          const { data, error } = await supabase.functions.invoke('get-project-collaborators', {
            body: { projectId },
          });

          if (error) {
            throw error;
          }

          if (!data || !data.data) {
            return [];
          }

          // Transform the data
          const collaborators: Collaborator[] = data.data.map((collab: any) => ({
            id: collab.id,
            project_id: collab.project_id,
            user_id: collab.user_id,
            role: collab.role,
            users_with_details: collab.users_with_details || null,
          }));

          // Cache user details
          collaborators.forEach(collab => {
            if (collab.user_id && collab.users_with_details) {
              cacheUser(collab.user_id, {
                id: collab.user_id,
                email: collab.users_with_details.email || '',
                display_name: collab.users_with_details.display_name || '',
                avatar_url: collab.users_with_details.avatar_url || undefined,
              });
            }
          });

          // Store in IndexedDB
          try {
            await db.collaborators.bulkPut(collaborators);
          } catch (storageError) {
            logger.error(`Error storing collaborators in IndexedDB:`, storageError);
            // Continue even if storage fails
          }

          return collaborators;
        },
      });

      logger.store('Successfully preloaded collaborators', { projectId });
    } catch (error) {
      logger.error('Failed to preload collaborators', {
        error: error instanceof Error ? error.message : String(error),
        projectId,
      });
      // Don't throw - preloading should be non-blocking
    }
  };

  return { collaborators, isLoading, isFetching, preload };
}
